import React, { useState } from 'react';
import { adminInitiateLogin, adminVerifyOTP } from '../services/api';

export default function AdminLogin({ onLoginSuccess }) {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInitiate = async () => {
    if (!username.trim()) return setError('Please enter your username.');
    setLoading(true); setError('');
    try {
      const res = await adminInitiateLogin(username.trim());
      setSessionId(res.data.sessionId);
      setMaskedEmail(res.data.email);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (!otp.trim()) return setError('Please enter the OTP.');
    setLoading(true); setError('');
    try {
      const res = await adminVerifyOTP(sessionId, otp.trim());
      localStorage.setItem('nb_admin_token', res.data.token);
      localStorage.setItem('nb_admin', JSON.stringify(res.data.admin));
      onLoginSuccess(res.data.admin);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Segoe UI', Arial, sans-serif"
    }}>
      <div style={{ display: 'flex', width: '100%', maxWidth: 900, minHeight: 520, borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}>

        {/* Left Panel */}
        <div style={{
          flex: 1, background: 'linear-gradient(160deg, #1e3a5f 0%, #2563eb 100%)',
          padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 48 }}>
              <div style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>N</span>
              </div>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Navia Backup</span>
            </div>
            <h2 style={{ color: '#fff', fontSize: 26, fontWeight: 700, margin: '0 0 12px', lineHeight: 1.3 }}>
              RMS Admin<br />Control Center
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 1.7, margin: 0 }}>
              Manage exchange segments, monitor square-off operations, and communicate with clients during platform downtime.
            </p>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: 'Segments Managed', value: '5' },
              { label: 'Exchanges', value: 'NSE · BSE · MCX' },
              { label: 'Access Level', value: 'Full Admin' },
            ].map(item => (
              <div key={item.label} style={{
                background: 'rgba(255,255,255,0.1)', borderRadius: 10,
                padding: '12px 16px', display: 'flex', justifyContent: 'space-between'
              }}>
                <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>{item.label}</span>
                <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel */}
        <div style={{ flex: 1, background: '#fff', padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <h3 style={{ color: '#0f172a', fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>
            {step === 1 ? 'Sign In' : 'Verify OTP'}
          </h3>
          <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 32px' }}>
            {step === 1 ? 'Enter your admin credentials to access the portal' : `OTP sent to ${maskedEmail}`}
          </p>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
            {[1, 2].map(s => (
              <div key={s} style={{
                flex: 1, height: 3, borderRadius: 2,
                background: step >= s ? '#2563eb' : '#e2e8f0'
              }} />
            ))}
          </div>

          {error && (
            <div style={{
              background: '#fff5f5', border: '1px solid #fed7d7',
              borderRadius: 8, padding: '10px 14px', marginBottom: 20,
              color: '#c53030', fontSize: 13
            }}>{error}</div>
          )}

          {step === 1 ? (
            <>
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#374151', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                  USERNAME
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleInitiate()}
                  placeholder="Enter your username"
                  style={{
                    width: '100%', padding: '11px 14px', borderRadius: 8,
                    border: '1.5px solid #e2e8f0', background: '#f8fafc',
                    color: '#0f172a', fontSize: 14, outline: 'none',
                    boxSizing: 'border-box', transition: 'border 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              <button
                onClick={handleInitiate}
                disabled={loading}
                style={{
                  width: '100%', padding: '12px', borderRadius: 8, border: 'none',
                  background: loading ? '#93c5fd' : '#2563eb', color: '#fff',
                  fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(37,99,235,0.3)'
                }}
              >
                {loading ? 'Sending OTP...' : 'Send OTP →'}
              </button>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#374151', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                  ONE-TIME PASSWORD
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleVerify()}
                  placeholder="000000"
                  maxLength={6}
                  style={{
                    width: '100%', padding: '14px', borderRadius: 8,
                    border: '1.5px solid #e2e8f0', background: '#f8fafc',
                    color: '#0f172a', fontSize: 28, fontWeight: 700,
                    letterSpacing: 10, textAlign: 'center', outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
                <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 8 }}>
                  Valid for 24 hours · Check your registered email
                </p>
              </div>
              <button
                onClick={handleVerify}
                disabled={loading}
                style={{
                  width: '100%', padding: '12px', borderRadius: 8, border: 'none',
                  background: loading ? '#86efac' : '#16a34a', color: '#fff',
                  fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(22,163,74,0.3)', marginBottom: 12
                }}
              >
                {loading ? 'Verifying...' : 'Verify & Login →'}
              </button>
              <button
                onClick={() => { setStep(1); setOtp(''); setError(''); }}
                style={{
                  width: '100%', padding: '10px', borderRadius: 8,
                  border: '1.5px solid #e2e8f0', background: '#fff',
                  color: '#64748b', fontSize: 13, cursor: 'pointer'
                }}
              >
                ← Back
              </button>
            </>
          )}

          <div style={{ marginTop: 32, paddingTop: 16, borderTop: '0.5px solid #e2e8f0' }}>
  <p style={{ color: '#94a3b8', fontSize: 10, textAlign: 'center', marginBottom: 8, fontWeight: 600 }}>
    Navia Markets Ltd · Authorised Personnel Only
  </p>
  <p style={{ color: '#cbd5e1', fontSize: 9, textAlign: 'left', lineHeight: 1.7, margin: 0 }}>
    SEBI Registration No. INZ000095034 – NSE Member code for CM, FAO & CD: 07708 – BSE Clearing No. for CM, FAO & CD: 6341 – MCX Member ID: 45345 – NSDL DP ID: IN300378 – CDSL DP ID: 12086500 – SEBI Registration No. for DP: IN-DP-311-2017 – GST Registration number: 33AAACM4739R2ZZ – Address: 9th Floor, Calavala's Centenary Centre, Door No.9, Club House Road, Chennai – 600 002. Email ID: ig@naviamarkets.com – Contact No. +91-44-39189449
  </p>
  <p style={{ color: '#cbd5e1', fontSize: 9, textAlign: 'left', lineHeight: 1.7, marginTop: 6 }}>
    <strong style={{ color: '#94a3b8' }}>Disclaimer:</strong> Investment in securities market are subject to market risks, read all the related documents carefully before investing. The securities quoted are exemplary and are not recommendatory. Brokerage will not exceed the SEBI prescribed limit.
  </p>
</div>
        </div>
      </div>
    </div>
  );
}